import React, { useState, useEffect, useRef } from "react";
import {
  Download,
  ExternalLink,
  Link2,
  Sparkles,
  Upload,
  Table2,
  FileText,
  Users,
  Unlock,
  Mail,
} from "lucide-react";
import FecUploadZone from "./FecUploadZone";
import { openCrmPresentation } from "./crmGenerate";
// import { openCrmDownload } from "./crmDownload";
import { generateCrmHtml } from "./crmGenerateHtml.js";
import CrmDashboard from "./Suivibilan/dashboard.js";
import { useEtatGlobal } from "../EtatGlobal.js";
import { authFetch, httpClient } from '../../HttpClient';
import { showStatusToast } from "../../components/ui/Toast";
import LiaisonAcd, { CHAMPS_LIAISON, estCollabNiv1 } from "../Portefeuille/LiaisonAcd";
import { uploadFileToBlob } from "../../utils/uploadFileToBlob";

import { dialog } from "../../components/ui/Dialog";
// FEC N / N-1 depuis ACD (CRM, réservé aux Administrateurs et au
// Collaborateur Niv. 1 du dossier) :
// le dossier doit être lié à ACD (même fenêtre de liaison que la fiche
// client) ; les fichiers récupérés sont déjà stockés côté serveur et passent
// par le même calcul qu'un dépôt manuel (backend acdController.recupererFec).
const ROLES_ACD_CRM = ["ADMINISTRATEUR"];

const Claude = ({ selectedClient, selectedYear, onClientUpdated }) => {
  const [fileFecn, setFileFecn] = useState(null);
  const [fileFec1, setFileFec1] = useState(null);
  const [fileEmprunt, setFileEmprunt] = useState(null);
  const [fileImmobi, setFileImmobi] = useState(null);
  const [fileDsn, setFileDsn] = useState(null);
  const [loadingEmail, setLoadingEmail] = useState(false);
  const { utilisateur } = useEtatGlobal();
  const [showPromptEditor, setShowPromptEditor] = useState(false);
  const [systemText, setSystemText] = useState(
    `Tu es un expert-comptable senior au sein du cabinet Alfa Comptabilité Audit, spécialisé dans l'analyse financière des TPE et PME réunionnaises soumises à l'impôt sur les sociétés (IS).

  Ta mission est de rédiger le Compte Rendu de Mission (CRM) annuel d'un client à partir des indicateurs financiers calculés et des commentaires transmis par le collaborateur en charge du dossier ( tout cela dans les données en JSON ).

  ---

  ## RÔLE ET POSTURE

  Tu t'exprimes en expert-comptable qui s'adresse directement à son client chef d'entreprise. Ton ton est expert, direct et professionnel — tu nommes les chiffres, tu portes un regard analytique, tu ne banalises pas. Tu évites le jargon comptable inutile sans pour autant vulgariser à l'excès : le client est un dirigeant capable de comprendre EBE, BFR, ratio d'endettement.

  ---

  ## STRUCTURE DE TA RÉPONSE

  Tu retournes UNIQUEMENT un objet JSON valide, sans aucun texte avant ou après. Aucun commentaire, aucune introduction, aucun markdown. Juste le JSON brut.

  La structure est la suivante :

  {
    "vue_ensemble": {
      "analyse": "...",
      "informations_recommandations": "..."
    },
    "activite": {
      "analyse": "...",
      "informations_recommandations": "..."
    },
    "tresorerie": {
      "analyse": "...",
      "informations_recommandations": "..."
    },
    "financement_investissement": {
      "analyse": "...",
      "informations_recommandations": "..."
    },
    "social_personnel": {
      "analyse": "...",
      "informations_recommandations": "..."
    },
    "etat": {
      "analyse": "...",
      "informations_recommandations": "..."
    }
  }

  ## IMPORTANT : retourne strictement un objet JSON valide.
  - Ne mets jamais le JSON sous forme de texte ou de chaîne.
  - Ne mets aucun guillemet supplémentaire autour du JSON global.
  - Ne mets aucun texte, commentaire, titre ou markdown avant ou après le JSON.
  - Chaque champ doit être une chaîne de caractères ou un nombre, aucun tableau ou objet supplémentaire inutile.
  - JSON formaté correctement : pas de backslash \n à l’intérieur, toutes les guillemets doivent être doubles.

  ---

  ## CONTENU DE CHAQUE CHAMP

  ### analyse (tile Analyse — fond indigo)
  - Synthèse factuelle et analytique des indicateurs financiers du panel concerné
  - Compare systématiquement N vs N-1 en valeur absolue ET en pourcentage d'évolution
  - Identifie les tendances positives, les points de vigilance, les signaux faibles
  - Longueur : 4 à 6 phrases formant un paragraphe continu, sans listes à puces
  - Ton : expert, factuel, analytique

  ### informations_recommandations (tile Informations & Recommandations — fond cramoisi)
  - S'appuie sur les commentaires transmis par le collaborateur pour personnaliser le message
  - Si les commentaires collaborateur apportent un contexte (investissement, recrutement, événement exceptionnel), intègre-le explicitement
  - Formule des recommandations concrètes et engagées : "Nous recommandons de...", "Il convient de...", "Anticiper..."
  - Longueur : 4 à 6 phrases formant un paragraphe continu, sans listes à puces
  - Ton : engagé, actionnable, orienté vers la décision du dirigeant

  ---

  ## CONTENU PAR PANEL

  ### vue_ensemble
  Synthèse globale de l'exercice : croissance du CA, évolution de la rentabilité (EBE, résultat net), situation de trésorerie. Donne une lecture d'ensemble avant d'entrer dans le détail des panels suivants.

  ### activite
  Analyse du compte de résultat : CA, marges (commerciale, VA), structure des charges (achats, charges externes, personnel). Met en lumière les leviers de la performance ou les points de compression.

  ### tresorerie
  Analyse de la position de trésorerie à la clôture, de son évolution vs N-1, et des éléments qui l'expliquent (BFR, délais clients/fournisseurs, investissements). Alerte si la situation se dégrade.

  ### financement_investissement
  Analyse de la structure financière : emprunts en cours (capital restant, échéances, taux), immobilisations (acquisitions, vétusté du parc), comptes courants associés. Apprécie la capacité de remboursement au regard de l'EBE.

  ### social_personnel
  Analyse des données sociales : évolution des effectifs, masse salariale, coût moyen par salarié. Croise avec les données DSN si disponibles. Signale tout écart notable.

  ### etat
  Analyse de la situation fiscale et sociale de la société : charge d'IS, cohérence avec le résultat fiscal, absence de dette fiscale anormale. Anticipe les obligations à venir (acomptes, déclarations).

  ---

  ## RÈGLES ABSOLUES

  1. Tu ne mentionnes JAMAIS de chiffres que tu n'as pas reçus dans les données. Si une donnée est absente (null ou non transmise), tu l'indiques sobrement ("donnée non disponible") sans improviser.
  2. Tu n'inventes pas de secteur d'activité, de contexte ou d'événement non mentionné.
  3. Si les commentaires collaborateur sont vides ou absents, tu rédiges le champ informations_recommandations à partir des seuls indicateurs financiers, sans le signaler au client.
  4. Tu n'utilises jamais de listes à puces ni de titres dans les valeurs JSON — uniquement du texte en prose.
  5. Tu exprimes les montants en milliers d'euros (K€) avec exactement deux décimales (format : 0,00 K€), sans arrondi à l’unité.
  6. Tu ne mentionnes jamais Claude, l'IA, ou le fait que ce texte est généré automatiquement.
  7. Le JSON retourné doit être strictement valide (pas de virgule finale, pas de commentaire, guillemets doubles uniquement).
  `
  );
  const [isLocked, setIsLocked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [recupAcd, setRecupAcd] = useState(false);
  const liaisonAcdRef = useRef(null);
  const clientCrm = selectedClient?.client;
  const acdCrm = ROLES_ACD_CRM.includes(utilisateur?.role) || estCollabNiv1(clientCrm, utilisateur);

  // Les FEC récupérés depuis ACD valent pour un dossier et une année : on les
  // retire si l'un des deux change (un dépôt manuel reste en place).
  useEffect(() => {
    setFileFecn((f) => (f?.acd ? null : f));
    setFileFec1((f) => (f?.acd ? null : f));
  }, [clientCrm?.id, selectedYear]);

  const recupererFecAcd = async (codeLie) => {
    if (!codeLie && !clientCrm?.codeACD) {
      liaisonAcdRef.current?.ouvrir(); // la récupération suit la liaison (onLie)
      return;
    }
    setRecupAcd(true);
    try {
      const { data } = await httpClient.post(
        `https://backend-myalfa.vercel.app/api/acd/clients/${clientCrm.id}/fec`,
        { annee: selectedYear },
      );
      const { n, n1 } = data.fichiers || {};
      setFileFecn((f) => (n ? { acd: true, ...n } : f?.acd ? null : f));
      setFileFec1((f) => (n1 ? { acd: true, ...n1 } : f?.acd ? null : f));
      setError(null);
      const recus = [n && `FEC N (${n.exercice.fin.slice(0, 4)})`, n1 && `FEC N-1 (${n1.exercice.fin.slice(0, 4)})`].filter(Boolean);
      showStatusToast({
        status: "success",
        titre: "Fichiers récupérés depuis ACD",
        message: `${recus.join(" et ")} prêt${recus.length > 1 ? "s" : ""} pour l'analyse.${data.avertissements?.length ? ` ${data.avertissements.join(" ")}` : ""}`,
      });
    } catch (err) {
      showStatusToast({
        status: "error",
        titre: "Récupération depuis ACD impossible",
        message: `${err?.response?.data?.message || "ACD n'a pas pu fournir les fichiers."} Vous pouvez toujours déposer les FEC à la main.`,
      });
    } finally {
      setRecupAcd(false);
    }
  };

  // Liaison faite depuis le CRM : seules les informations de la fiche que la
  // liaison modifie remontent au Suivi Bilan (sélection, tableau, fiche).
  const majClientLiaison = (c) =>
    onClientUpdated?.(Object.fromEntries(CHAMPS_LIAISON.filter((k) => k in c).map((k) => [k, c[k]])));
  const [indicators, setIndicators] = useState(null);
  const [error, setError] = useState(null);
  const [step, setStep] = useState(0);
  const [progress, setProgress] = useState(0);
  const [editableResult, setEditableResult] = useState(null);
  const [isDirty, setIsDirty] = useState({
    analyse: false,
    informations_recommandations: false,
    analyseActvite: false,
    informations_recommandationsActivite: false,
    analyseTresorerie: false,
    informations_recommandationsTresorerie: false,
    analyseFinancement: false,
    informations_recommandationsFinancement: false,
    analyseSocial: false,
    informations_recommandationsSocial: false,
    analyseEtat: false,
    informations_recommandationsEtat: false,
  });
  const [isUpdating, setIsUpdating] = useState(false);
  const [isSaved, setIsSaved] = useState(false);

  useEffect(() => {
    setEditableResult(result);
    setIsDirty(false);
  }, [result]);

  useEffect(() => {
    const bilan = selectedClient?.client?.suiviBilans?.find(
      (b) => b.annees === selectedYear
    );

    const hasIndicators =
      bilan?.indicators && Object.keys(bilan.indicators || {}).length > 0;

    const hasResult =
      bilan?.result && Object.keys(bilan.result || {}).length > 0;

    if (hasIndicators) {
      setIndicators(bilan.indicators);
    }

    if (hasResult) {
      setResult(bilan.result);
    }

    setIsLocked(hasIndicators && hasResult);
  }, [selectedClient, selectedYear]);

  const steps = [
    "Génération des données",
    "Analyse IA des fichiers",
    "Génération IA",
    "Sauvegarde du bilan",
  ];

  console.log("Selected client in Claude id:", selectedClient.client.id);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isLocked) return; // 🔒 bloque totalement

    if (!fileFecn && !fileFec1) {
      setError("Veuillez fournir au moins un fichier (fecn ou fec1)");
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);

    const formData = new FormData();

    formData.append("raison_sociale", selectedClient?.client?.nom);
    formData.append(
      "forme_juridique",
      selectedClient?.client?.formeSociale?.[0]
    );
    formData.append("secteur_activite", selectedClient?.client?.activite);

    const commentaires =
      selectedClient?.client?.suiviBilans
        ?.find((b) => b.annees === selectedYear)
        ?.commentaires?.map((c) => ({
          cycle: c.type,
          texte: c.commentaire,
        })) || [];

    formData.append("commentaires", JSON.stringify(commentaires));

    try {
      // Upload direct navigateur → Blob (contourne la limite 4,5 Mo) — les
      // fichiers FEC/PDF peuvent être volumineux, d'où une progression
      // affichée avant même l'appel à GenerateData.
      const filesToUpload = [
        ["fecn", fileFecn],
        ["fec1", fileFec1],
        ["emprunts", fileEmprunt],
        ["immobilisations", fileImmobi],
        ["dsn", fileDsn],
      ].filter(([, file]) => file);

      for (const [key, file] of filesToUpload) {
        // FEC récupéré depuis ACD : déjà stocké côté serveur.
        const url = file.acd ? file.url : await uploadFileToBlob(
          file,
          "https://backend-myalfa.vercel.app/api/GenerateData/upload-token",
          ({ percentage }) => setProgress(Math.round(percentage * 0.2))
        );
        formData.append(`${key}Url`, url);
      }

      setStep(1);
      setProgress(20);

      // 🔹 ÉTAPE 1 : GenerateData
      const response = await authFetch("https://backend-myalfa.vercel.app/api/GenerateData",
        {
          method: "POST",
          body: formData,
        }
      );

      if (!response.ok) {
        throw new Error("Erreur lors de l'appel à l'API");
      }

      const data = await response.json();
      const indicatorsData = data?.indicators;

      setIndicators(indicatorsData);

      setStep(2);
      setProgress(50);

      // 🔹 ÉTAPE 2 : IA
      const iaResponse = await authFetch("https://backend-myalfa.vercel.app/api/ResumeIAGenerate",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            indicators: indicatorsData,
            system: systemText,
          }),
        }
      );

      if (!iaResponse.ok) {
        throw new Error("Erreur lors de la génération IA");
      }

      const iaData = await iaResponse.json();

      const resultText = iaData?.result?.[0]?.text || "{}";

      let resultJson;
      try {
        resultJson = JSON.parse(resultText);
      } catch (err) {
        resultJson = { raw: resultText };
      }

      setResult(resultJson);

      // 🔹 ÉTAPE 3 : PUT suiviBilan
      const putResponse = await authFetch(`https://backend-myalfa.vercel.app/api/suiviBilan/${selectedClient.client.id}/${selectedYear}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            indicators: indicatorsData,
            result: resultJson,
          }),
        }
      );

      if (!putResponse.ok) {
        throw new Error("Erreur lors de la mise à jour du suivi bilan");
      }

      // =========================================================
      // 🔹 ÉTAPE 4 : UPLOAD HTML CRM
      // =========================================================

      const { html, fileName } = generateCrmHtml(indicatorsData, resultJson);
      const blob = new Blob([html], { type: "text/html" });

      const htmlFormData = new FormData();
      htmlFormData.append("htmlFile", blob, fileName);

      const uploadRes = await authFetch("https://backend-myalfa.vercel.app/api/upload-crm-html",
        {
          method: "POST",
          body: htmlFormData,
        }
      );

      if (!uploadRes.ok) {
        throw new Error("Erreur lors de l'upload HTML CRM");
      }

      const uploadData = await uploadRes.json();
      const fileUrl = uploadData?.fileUrl;

      // =========================================================
      // 🔹 ÉTAPE 5 : HISTORIQUE CLIENT
      // =========================================================

      try {
        await authFetch("https://backend-myalfa.vercel.app/api/historique-client", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            titre: "Création de Compte Rendu",
            description: fileUrl,
            clientId: selectedClient.client.id,
            eventId: null,
          }),
        });

        console.log("Historique client créé avec succès !");
      } catch (histError) {
        console.error(
          "Erreur lors de la création de l'historique client :",
          histError
        );
      }

      setStep(4);
      setProgress(100);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const updateResultJson = async (resultJson) => {
    setIsUpdating(true);
    setIsSaved(false);

    try {
      const res = await authFetch(`https://backend-myalfa.vercel.app/api/suiviBilan/${selectedClient.client.id}/${selectedYear}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            result: resultJson,
          }),
        }
      );

      if (!res.ok) {
        throw new Error("Erreur lors de la mise à jour du résultat");
      }

      const data = await res.json();

      setIsSaved(true); // ✅ succès visuel
      return data;
    } finally {
      setIsUpdating(false);
    }
  };

  const sendPresentationEmail = async () => {
    try {
      setLoadingEmail(true);
      const { html, fileName } = generateCrmHtml(indicators, result);

      const blob = new Blob([html], { type: "text/html" });

      const formData = new FormData();

      const client = indicators?.client || {};
      const cr = indicators?.compte_de_resultat || {};
      const bilan = indicators?.bilan || {};

      // ✅ fichier HTML réel
      formData.append("htmlFile", blob, fileName);

      formData.append("id", selectedClient?.client?.id || "");

      formData.append("nomUser", selectedClient?.client?.nom || "");
      formData.append("exerciceN", client.exercice_N || "N");
      formData.append("exerciceN1", client.exercice_N1 || "N-1");

      formData.append("caN", cr.ca_N || 0);
      formData.append("caVarPct", cr.ca_var_pct || 0);

      formData.append("resultatNetN", cr.resultat_net_N || 0);
      formData.append("resultatNetVarPct", cr.resultat_net_var_pct || 0);

      formData.append("tresorerieN", bilan.tresorerie_N || 0);
      formData.append("tresorerieVarPct", bilan.tresorerie_var_pct || 0);

      formData.append("vue_ensemble", result.vue_ensemble.analyse || "");

      formData.append("emailUsers", selectedClient?.client?.email || "");

      const res = await authFetch("https://backend-myalfa.vercel.app/api/send-email-crm",
        {
          method: "POST",
          body: formData,
        }
      );

      if (!res.ok) {
        throw new Error("Erreur envoi email");
      }

      dialog.alert("Email envoyé avec succès 🚀");
    } catch (err) {
      console.error(err);
      dialog.alert("Erreur lors de l'envoi ❌");
    } finally {
      setLoadingEmail(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 w-full">
      {/* Flex container horizontal */}
      <form
        onSubmit={handleSubmit}
        className="bg-white shadow-xl rounded-2xl p-4 md:p-6 w-full flex flex-col gap-4"
      >
        {/* Container global pour les fichiers */}
        <div
          className="bg-white rounded-xl p-2 w-full overflow-hidden"
          style={{ borderColor: "#E6E3EC" }}
        >
          <div className="flex items-center justify-between gap-3">
            <div className="flex min-w-0 items-center gap-2.5">
              <h3
                className="text-sm font-semibold"
                style={{ color: "#1A1525" }}
              >
                Dépôt des fichiers de {selectedClient?.client?.nom} -{" "}
                {selectedClient?.client.formeSociale[0]} -{" "}
                {selectedClient?.client.activite} -{" "}
                {selectedClient?.client?.email}
              </h3>
            </div>
            {clientCrm && (
              <div className="flex min-w-0 shrink-0 items-center text-xs">
                <LiaisonAcd
                  ref={liaisonAcdRef}
                  client={clientCrm}
                  utilisateur={utilisateur}
                  onClientUpdated={majClientLiaison}
                  rolesLiaison={ROLES_ACD_CRM}
                  onLie={(code) => recupererFecAcd(code)}
                  nonLie={
                    <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-500">
                      Non lié à ACD
                    </span>
                  }
                />
              </div>
            )}
          </div>

          <div className="flex gap-3 w-full flex-wrap">
            {/* FEC N-1 */}
            <FecUploadZone
              label="FEC N-1"
              period="Exercice Année-1"
              file={fileFec1}
              onFileChange={setFileFec1}
              icon={Upload}
              chargement={recupAcd}
            />

            {/* FEC N */}
            <FecUploadZone
              label="FEC N"
              period="Exercice Année"
              file={fileFecn}
              onFileChange={setFileFecn}
              icon={Upload}
              chargement={recupAcd}
            />

            <FecUploadZone
              label={"Liste des immobilisations"}
              file={fileImmobi}
              onFileChange={setFileImmobi}
              icon={Table2}
            />

            <FecUploadZone
              label={"Tableau des emprunts"}
              file={fileEmprunt}
              onFileChange={setFileEmprunt}
              icon={FileText}
            />

            <FecUploadZone
              label={"Récap DSN"}
              file={fileDsn}
              onFileChange={setFileDsn}
              icon={Users}
            />
          </div>

          {acdCrm && clientCrm && (
            <div className="mt-3 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-[#840040]/15 bg-gradient-to-r from-[#840040]/[0.06] to-transparent px-3 py-2.5">
              <p className="flex min-w-0 items-center gap-2 text-xs text-slate-600">
                <Link2 className="h-3.5 w-3.5 shrink-0 text-[#840040]" aria-hidden="true" />
                {clientCrm.codeACD
                  ? `En cliquant ici, les documents (FEC ${selectedYear} et ${Number(selectedYear) - 1}) seront récupérés directement depuis ACD.`
                  : "Vous devez d'abord lier ce dossier client à un dossier ACD."}
              </p>
              <button
                type="button"
                onClick={() => recupererFecAcd()}
                disabled={recupAcd || loading || isLocked}
                className="inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-[#840040] px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-[#6d0035] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#840040]/40 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Download className="h-3.5 w-3.5" aria-hidden="true" />
                {recupAcd ? "Récupération en cours…" : "Récupérer les fichiers via ACD"}
              </button>
            </div>
          )}
        </div>
        {error && (
          <p className="mt-4 text-red-600 font-medium text-center">{error}</p>
        )}
      </form>

      <div className="mt-6 flex justify-center gap-10">
        <button
          type="submit"
          disabled={loading}
          onClick={handleSubmit}
          className={`group flex justify-center items-center gap-2 px-6 py-2.5 rounded-xl border-2 font-semibold text-sm transition-all duration-200 text-center ${
            loading || isLocked ? "opacity-40 cursor-not-allowed" : ""
          }`}
          style={{
            borderColor: "#840040",
            color: loading ? "#fff" : "#840040",
            backgroundColor: loading ? "#840040" : "#fff",
          }}
          onMouseEnter={(e) => {
            if (!loading) {
              e.currentTarget.style.backgroundColor = "#840040";
              e.currentTarget.style.color = "#fff";
            }
          }}
          onMouseLeave={(e) => {
            if (!loading) {
              e.currentTarget.style.backgroundColor = "#fff";
              e.currentTarget.style.color = "#840040";
            }
          }}
        >
          {loading ? (
            <div className="w-full max-w-xl bg-white border border-gray-200 shadow-lg rounded-2xl p-5">
              {/* HEADER STEP */}
              <div className="flex justify-between items-center mb-3">
                <div className="text-sm font-semibold text-gray-800">
                  {steps[step - 1] || "Initialisation..."}
                </div>

                <div className="text-xs font-medium text-gray-500">
                  {progress}%
                </div>
              </div>

              {/* PROGRESS BAR LARGE + GLOW */}
              <div className="relative w-full h-4 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-700 ease-out"
                  style={{
                    width: `${progress}%`,
                    background:
                      "linear-gradient(90deg, #840040, #b30059, #ff2e88)",
                  }}
                />

                {/* shimmer effect */}
                <div className="absolute top-0 left-0 w-full h-full opacity-30 animate-pulse bg-gradient-to-r from-transparent via-white to-transparent" />
              </div>

              {/* STEPS INDICATOR */}
              <div className="flex justify-between mt-4 text-[11px] text-gray-500">
                {steps.map((s, i) => (
                  <div
                    key={i}
                    className={`transition-all ${
                      i < step
                        ? "text-[#840040] font-semibold"
                        : "text-gray-400"
                    }`}
                  >
                    {s}
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <>
              <>
                <Sparkles className="w-4 h-4" />
                Générer le Compte Rendu de Mission
              </>
            </>
          )}
        </button>
        {["ADMINISTRATEUR", "EXPERT_COMPTABLE"].includes(utilisateur.role) && (
          <button
            onClick={() => setShowPromptEditor(!showPromptEditor)}
            className="flex items-center gap-2 px-4 py-2 rounded-xl border text-sm font-semibold transition-all duration-200"
            style={{ borderColor: "#840040", color: "#840040" }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "#840040";
              e.currentTarget.style.color = "#fff";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "transparent";
              e.currentTarget.style.color = "#840040";
            }}
          >
            {showPromptEditor ? "Fermer le prompt" : "Modifier le Prompt"}
          </button>
        )}
      </div>

      {result && indicators && (
        <>
          {/* ACTIONS (droite + colonne) */}
          <div className="w-full flex justify-end mt-10">
            <div className="flex flex-col items-end gap-3">
              {/* Bouton présentation */}
              <div className="flex w-full justify-end gap-5">
                <button
                  onClick={() => {
                    console.log("type result:", typeof result);
                    console.log("type indicators:", typeof indicators);
                    openCrmPresentation(indicators, result);
                  }}
                  className="flex items-center gap-2 px-4 py-2 rounded-xl border text-sm font-semibold transition-all duration-200 shadow-sm"
                  style={{ borderColor: "#7E1738", color: "#7E1738" }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = "#7E1738";
                    e.currentTarget.style.color = "#fff";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "transparent";
                    e.currentTarget.style.color = "#7E1738";
                  }}
                >
                  <ExternalLink className="w-4 h-4" />
                  Ouvrir la présentation client
                </button>

                {/* <button
                  onClick={() => {
                    console.log("type result:", typeof result);
                    console.log("type indicators:", typeof indicators);
                    openCrmDownload(indicators, result);
                  }}
                  className="flex items-center gap-2 px-4 py-2 rounded-xl border text-sm font-semibold transition-all duration-200 shadow-sm"
                  style={{ borderColor: "#7E1738", color: "#7E1738" }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = "#7E1738";
                    e.currentTarget.style.color = "#fff";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "transparent";
                    e.currentTarget.style.color = "#7E1738";
                  }}
                >
                  <Download className="w-4 h-4" />
                  Télécharger la présentation client
                </button> */}

                <button
                  onClick={sendPresentationEmail}
                  disabled={loadingEmail}
                  className={`flex items-center gap-2 px-4 py-2 rounded-xl border text-sm font-semibold transition-all duration-200 shadow-sm ${
                    loadingEmail ? "opacity-60 cursor-not-allowed" : ""
                  }`}
                  style={{
                    borderColor: "#15803d",
                    color: loadingEmail ? "#999" : "#15803d",
                    backgroundColor: loadingEmail ? "#f3f3f3" : "transparent",
                  }}
                  onMouseEnter={(e) => {
                    if (!loadingEmail) {
                      e.currentTarget.style.backgroundColor = "#15803d";
                      e.currentTarget.style.color = "#fff";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!loadingEmail) {
                      e.currentTarget.style.backgroundColor = "transparent";
                      e.currentTarget.style.color = "#15803d";
                    }
                  }}
                >
                  {loadingEmail ? (
                    <>
                      <svg
                        className="w-4 h-4 animate-spin"
                        viewBox="0 0 24 24"
                        fill="none"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8v8H4z"
                        />
                      </svg>
                      Envoi en cours...
                    </>
                  ) : (
                    <>
                      <Mail className="w-4 h-4" />
                      Publier avec MyALFA Link
                    </>
                  )}
                </button>
              </div>

              {/* Bouton unlock */}
              {isLocked && (
                <button
                  onClick={() => setIsLocked(false)}
                  className="flex items-center gap-2 px-4 py-2 rounded-xl border text-sm font-semibold transition-all duration-200 shadow-sm"
                  style={{
                    borderColor: "#b91c1c",
                    color: "#b91c1c",
                    backdropFilter: "blur(6px)",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = "#b91c1c";
                    e.currentTarget.style.color = "#fff";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor =
                      "rgba(185, 28, 28, 0.06)";
                    e.currentTarget.style.color = "#b91c1c";
                  }}
                >
                  <Unlock className="w-4 h-4" />
                  Déverrouiller et regénérer
                </button>
              )}
            </div>
          </div>
          <div className="w-full flex justify-start mt-3 mb-5">
            {isDirty && (
              <button
                onClick={async () => {
                  setResult(editableResult);
                  await updateResultJson(editableResult);
                  setIsDirty(false);
                }}
                disabled={isUpdating}
                className="px-4 py-2 rounded-xl text-sm font-semibold border transition-all duration-200 flex items-center gap-2"
                style={{
                  backgroundColor: "#ecfdf5", // vert très léger (base)
                  borderColor: "#86efac",
                  color: "#166534",
                  opacity: isUpdating ? 0.6 : 1,
                  cursor: isUpdating ? "not-allowed" : "pointer",
                }}
                onMouseEnter={(e) => {
                  if (!isUpdating) {
                    e.currentTarget.style.backgroundColor = "#16a34a"; // vert hover
                    e.currentTarget.style.color = "#ffffff";
                    e.currentTarget.style.borderColor = "#16a34a";
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "#ecfdf5";
                  e.currentTarget.style.color = "#166534";
                  e.currentTarget.style.borderColor = "#86efac";
                }}
              >
                {isUpdating ? (
                  <>
                    <div className="w-3 h-3 border-2 border-t-transparent border-green-600 rounded-full animate-spin" />
                    Mise à jour...
                  </>
                ) : isSaved ? (
                  "✔ Sauvegardé"
                ) : (
                  "Appliquer la modification : Analyse et Informations & Recommandations"
                )}
              </button>
            )}
          </div>

          {/* Textarea conditionnel */}
          {showPromptEditor && (
            <div className="w-full mb-2">
              <label className="block text-sm font-semibold mb-2 text-gray-700">
                Prompt système
              </label>

              <textarea
                value={systemText}
                onChange={(e) => setSystemText(e.target.value)}
                rows={15}
                className="w-full p-3 border rounded-xl text-sm font-mono shadow-sm focus:outline-none focus:ring-2 focus:ring-[#840040]"
                placeholder="Modifier le system prompt ici..."
              />
            </div>
          )}

          <CrmDashboard
            indicators={indicators}
            result={result}
            isDirty={isDirty}
            setIsDirty={setIsDirty}
            editableResult={editableResult}
            setEditableResult={setEditableResult}
          />
          {/* RESULT JSON */}
        </>
      )}
    </div>
  );
};

export default Claude;
